import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { todayIsoBrt } from '@/lib/dateBrt';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Blocks,
  Search,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowUpDown,
  RefreshCw,
  Layers,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import logger from '@/lib/logger';
import { toast } from 'sonner';

const formatPct = (v) => `${(v ?? 0).toFixed ? v.toFixed(1) : v}%`;

function BlockAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('applications');
  const [sortDir, setSortDir] = useState('desc');
  const [filterUsed, setFilterUsed] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/phase-blocks/analytics');
      setData(res.data);
    } catch (e) {
      logger.error('Error fetching block analytics:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = useMemo(() => {
    if (!data?.blocks) return [];
    const q = search.trim().toLowerCase();
    let list = data.blocks;
    if (filterUsed) list = list.filter((b) => b.applications > 0);
    if (q) {
      list = list.filter(
        (b) =>
          (b.name || '').toLowerCase().includes(q) ||
          (b.category || '').toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      const va = a[sortKey] ?? 0;
      const vb = b[sortKey] ?? 0;
      if (typeof va === 'string' || typeof vb === 'string') {
        return sortDir === 'asc'
          ? String(va).localeCompare(String(vb), 'pt-BR')
          : String(vb).localeCompare(String(va), 'pt-BR');
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [data, search, sortKey, sortDir, filterUsed]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleDownload = async (format) => {
    try {
      toast.info(`Gerando ${format.toUpperCase()}...`);
      const params = new URLSearchParams();
      if (filterUsed) params.append('only_used', 'true');
      const res = await api.get(
        `/export/block-analytics/${format}?${params.toString()}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = todayIsoBrt();
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `block_analytics_${today}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exportado!`);
    } catch (e) {
      logger.error('Export error:', e);
      toast.error('Erro ao exportar');
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const { summary } = data;

  const SortHeader = ({ keyName, label, align = 'center' }) => (
    <th
      className={`py-3 px-3 font-semibold text-gray-900 text-${align} cursor-pointer select-none hover:text-emerald-700`}
      onClick={() => handleSort(keyName)}
      data-testid={`block-analytics-sort-${keyName}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown
          className={`w-3 h-3 ${sortKey === keyName ? 'text-emerald-700' : 'text-gray-400'}`}
        />
      </span>
    </th>
  );

  return (
    <div className="space-y-5" data-testid="block-analytics">
      {/* Summary KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-3">
            <Blocks className="w-7 h-7 text-emerald-700" />
            <div>
              <p className="text-xs text-emerald-700">Blocos ativos</p>
              <p className="text-2xl font-bold text-emerald-800" data-testid="block-analytics-total-blocks">
                {summary.total_blocks}
              </p>
              <p className="text-[10px] text-emerald-600">
                {summary.used_blocks} aplicado(s)
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-blue-200 bg-blue-50">
          <div className="flex items-center gap-3">
            <Layers className="w-7 h-7 text-blue-700" />
            <div>
              <p className="text-xs text-blue-700">Total de aplicações</p>
              <p className="text-2xl font-bold text-blue-800" data-testid="block-analytics-total-applications">
                {summary.total_applications}
              </p>
              <p className="text-[10px] text-blue-600">
                {summary.total_phases_created} fase(s) criadas
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-green-200 bg-green-50">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-7 h-7 text-green-700" />
            <div>
              <p className="text-xs text-green-700">Tarefas concluídas</p>
              <p className="text-2xl font-bold text-green-800">
                {summary.total_tasks_completed}
                <span className="text-xs text-green-600">/{summary.total_tasks}</span>
              </p>
              <p className="text-[10px] text-green-600">
                {formatPct(summary.overall_completion_rate)} no geral
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-red-700" />
            <div>
              <p className="text-xs text-red-700">Tarefas atrasadas</p>
              <p className="text-2xl font-bold text-red-800">
                {summary.total_tasks_late}
              </p>
              <p className="text-[10px] text-red-600">
                vencidas e não concluídas
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-3 border border-gray-200">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar bloco por nome ou categoria…"
              className="border-0 focus-visible:ring-0 shadow-none"
              data-testid="block-analytics-search"
            />
          </div>
          <Button
            variant={filterUsed ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterUsed((v) => !v)}
            className={filterUsed ? 'bg-emerald-700 hover:bg-emerald-800' : ''}
            data-testid="block-analytics-only-used"
          >
            {filterUsed ? 'Mostrando usados' : 'Apenas usados'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="block-analytics-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Recalcular
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownload('excel')}
            data-testid="block-analytics-export-excel"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownload('pdf')}
            data-testid="block-analytics-export-pdf"
          >
            <FileText className="w-4 h-4 mr-1" />
            PDF
          </Button>
        </div>
      </Card>

      {/* Top performers / bottlenecks shortcuts */}
      {rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(() => {
            const used = rows.filter((b) => b.tasks_total > 0);
            const topRate = [...used].sort((a, b) => b.completion_rate - a.completion_rate).slice(0, 3);
            const topLate = [...used].sort((a, b) => b.tasks_late - a.tasks_late).slice(0, 3).filter((b) => b.tasks_late > 0);
            return (
              <>
                <Card className="p-4 border border-emerald-200">
                  <h4 className="text-sm font-semibold text-emerald-800 flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4" />
                    Top 3 — maior taxa de conclusão
                  </h4>
                  {topRate.length === 0 ? (
                    <p className="text-xs text-gray-500">Ainda sem dados suficientes</p>
                  ) : (
                    <ul className="space-y-1 text-sm">
                      {topRate.map((b) => (
                        <li key={b.block_id} className="flex items-center justify-between gap-2">
                          <span className="truncate text-gray-800">{b.name}</span>
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                            {formatPct(b.completion_rate)}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>

                <Card className="p-4 border border-red-200">
                  <h4 className="text-sm font-semibold text-red-800 flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Top 3 — mais tarefas atrasadas
                  </h4>
                  {topLate.length === 0 ? (
                    <p className="text-xs text-gray-500">Nenhum bloco com tarefas atrasadas</p>
                  ) : (
                    <ul className="space-y-1 text-sm">
                      {topLate.map((b) => (
                        <li key={b.block_id} className="flex items-center justify-between gap-2">
                          <div className="truncate flex-1">
                            <p className="truncate text-gray-800">{b.name}</p>
                            {b.bottleneck_task && (
                              <p className="text-[11px] text-gray-500 truncate">
                                Gargalo: {b.bottleneck_task.name}
                              </p>
                            )}
                          </div>
                          <Badge className="bg-red-100 text-red-800 border-red-200">
                            {b.tasks_late}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </>
            );
          })()}
        </div>
      )}

      {/* Detailed table */}
      <Card className="p-0 border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <SortHeader keyName="name" label="Bloco" align="left" />
                <SortHeader keyName="applications" label="Aplicações" />
                <SortHeader keyName="phases_created" label="Fases" />
                <SortHeader keyName="tasks_total" label="Tarefas" />
                <SortHeader keyName="completion_rate" label="% Conclusão" />
                <SortHeader keyName="tasks_late" label="Atrasadas" />
                <SortHeader keyName="avg_days_open" label="Dias em aberto" />
                <th className="py-3 px-3 font-semibold text-gray-900 text-left">Gargalo</th>
              </tr>
            </thead>
            <tbody data-testid="block-analytics-table">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-gray-500">
                    Nenhum bloco corresponde aos filtros aplicados.
                  </td>
                </tr>
              ) : (
                rows.map((b) => (
                  <tr key={b.block_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`block-analytics-row-${b.block_id}`}>
                    <td className="py-3 px-3">
                      <div>
                        <p className="font-medium text-gray-900 truncate max-w-xs">{b.name}</p>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          {b.category && <Badge variant="outline" className="text-[10px]">{b.category}</Badge>}
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                            {b.task_template_count} tarefa(s) no template
                          </Badge>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center font-semibold text-blue-700">{b.applications}</td>
                    <td className="py-3 px-3 text-center text-gray-700">{b.phases_created}</td>
                    <td className="py-3 px-3 text-center text-gray-700">
                      <span className="text-green-700 font-medium">{b.tasks_completed}</span>
                      <span className="text-gray-400">/{b.tasks_total}</span>
                    </td>
                    <td className="py-3 px-3">
                      {b.tasks_total === 0 ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : (
                        <div className="flex items-center gap-2 justify-center">
                          <Progress value={b.completion_rate} className="h-2 w-16" />
                          <span className="font-bold text-emerald-700 text-xs">{formatPct(b.completion_rate)}</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">
                      {b.tasks_late > 0 ? (
                        <Badge className="bg-red-100 text-red-700">{b.tasks_late}</Badge>
                      ) : (
                        <span className="text-xs text-gray-400">0</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">
                      {b.avg_days_open > 0 ? (
                        <span className={`inline-flex items-center gap-1 text-xs ${b.avg_days_open > 30 ? 'text-red-700' : 'text-gray-700'}`}>
                          <Clock className="w-3 h-3" /> {b.avg_days_open}d
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-700 max-w-[180px] truncate">
                      {b.bottleneck_task ? (
                        <span title={b.bottleneck_task.name}>
                          {b.bottleneck_task.name}
                          <span className="text-gray-400"> ({b.bottleneck_task.late_count})</span>
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export default BlockAnalyticsTab;
